import React from 'react';
import {
  UserIcon,
  CpuChipIcon,
  BuildingOfficeIcon,
  LinkIcon,
  ArrowsRightLeftIcon,
} from '@heroicons/react/24/outline';
import { StatusPill, CARD, TEXT_MUTED, TEXT_PRIMARY, btn } from '../components';
import { formatPhoneNumber } from '../components/format';
import { countryLabel } from './constants';

/**
 * Assignment summary for a DID.
 *
 * The original returned a bag of Tailwind strings per type, which is how the
 * module ended up with blue/purple/amber accents nowhere else used. Types are
 * distinguished by icon and label; colour is reserved for status.
 */
export const assignmentOf = (did) => {
  const a = did.assignment;
  if (!a || a.type === 'unassigned') {
    return { label: 'Unassigned', icon: LinkIcon, muted: true };
  }

  switch (a.type) {
    case 'user': {
      const name =
        `${a.userId?.firstName || ''} ${a.userId?.lastName || ''}`.trim() ||
        a.userId?.email ||
        'User';
      return {
        label: name,
        sub: a.extension ? `Ext. ${a.extension}` : null,
        icon: UserIcon,
      };
    }
    case 'agent':
      return {
        label: a.agentId?.agentName || 'AI agent',
        sub: a.agentId?.department || null,
        icon: CpuChipIcon,
      };
    case 'department':
      return {
        label: String(a.department || '').toUpperCase() || 'Department',
        icon: BuildingOfficeIcon,
      };
    default:
      return { label: 'Unassigned', icon: LinkIcon, muted: true };
  }
};

const NumberCard = ({ did, onAssign, onUnassign, onRouting, onRelease }) => {
  const assignment = assignmentOf(did);
  const AssignIcon = assignment.icon;
  const assigned = !assignment.muted;

  return (
    <div className={`${CARD} p-4 flex flex-col gap-3`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className={`text-[15px] font-semibold tabular-nums truncate ${TEXT_PRIMARY}`}>
            {formatPhoneNumber(did.phoneNumber)}
          </p>
          <p className={`text-[12px] mt-0.5 truncate ${TEXT_MUTED}`}>
            {countryLabel(did.country)}
            {did.numberType ? ` · ${String(did.numberType).replace('_', '-')}` : ''}
          </p>
        </div>
        <StatusPill status={did.status} size="sm" />
      </div>

      {/* Who answers this number — the primary question for a DID list. */}
      <div className="flex items-center gap-2 min-w-0">
        <AssignIcon
          className={`w-4 h-4 flex-shrink-0 ${assigned ? 'text-gray-500 dark:text-gray-400' : 'text-gray-300 dark:text-gray-600'}`}
        />
        <div className="min-w-0">
          <p className={`text-[13px] truncate ${assigned ? TEXT_PRIMARY : TEXT_MUTED}`}>
            {assignment.label}
          </p>
          {assignment.sub && <p className={`text-[11px] ${TEXT_MUTED}`}>{assignment.sub}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button onClick={() => onAssign(did)} className={btn(assigned ? 'secondary' : 'primary', 'sm')}>
          {assigned ? 'Reassign' : 'Assign'}
        </button>
        {assigned && (
          <button onClick={() => onUnassign(did)} className={btn('ghost', 'sm')}>
            Unassign
          </button>
        )}
        <button
          onClick={() => onRouting(did)}
          className={btn('ghost', 'sm')}
          title="Call routing"
          aria-label="Call routing"
        >
          <ArrowsRightLeftIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => onRelease(did)}
          className={`${btn('ghost', 'sm')} ml-auto text-red-500 hover:bg-red-500/10`}
        >
          Release
        </button>
      </div>
    </div>
  );
};

export default NumberCard;
